import { rem } from 'polished'
import React, { useContext, useEffect } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'

import ActivityItemCard from './ActivityItemCard'
import CruiseItemCard from './Cruise'
import ExperienceItemCard from './ExperienceItemCard'
import HotelItemCard from './HotelItemCard'
import ItineraryItemCard from './ItineraryItemCard'
import TourItemCard from './Tour'
import TransportItemCard from './TransportItemCard'
import FlightSearchTile from '../SavedItemTiles/FlightSearchTile'
import TripItemSummary from '../TripModal/TripItems/Summary'

import ScrollAnchor from 'components/Common/ScrollAnchor'
import Heading from 'components/Luxkit/Typography/Heading'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import CarRentalItemCard from 'tripPlanner/components/ItemCard/CarItemCard'
import CardContainer from 'tripPlanner/components/ItemCard/Common/CardContainer'
import FlightItemCard from 'tripPlanner/components/ItemCard/Flight/FlightItemCard'
import NoteItemCard from 'tripPlanner/components/ItemCard/NoteItemCard'
import SectionIdProvider from 'tripPlanner/contexts/SectionIdProvider'
import TripContext from 'tripPlanner/contexts/TripContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { TripItem } from 'tripPlanner/types/tripItem'
import {
  formatDateTimeOrDateRange,
  formatMultiDatesDays,
  formatShortDateRange,
  isLeFlightItem,
} from 'tripPlanner/utils'
import { cardItemHoverAnimation } from 'tripPlanner/utils/css'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

export type ItemCardVariant = 'drawer' | 'tripSummary'

const Card = styled.div`
  display: flex;
  flex-direction: row;
  border: ${({ theme }) => `1px solid ${theme.palette.neutral.five}`};
  border-radius: ${(props) => props.theme.borderRadius.large};
  height: ${rem(88)};
  width: 100%;
  margin-bottom: ${rem(8)};
  user-select: none;
  touch-action: auto;
  align-items: center;
  background-color: ${(props) => props.theme.palette.neutral.eight};
  break-inside: avoid-page;
  ${cardItemHoverAnimation}
`

interface MappedStateProps {
  standaloneFlightsEnabled: boolean
}

interface Props {
  item: TripItem
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

function InnerItemCard({
  item,
  standaloneFlightsEnabled,
  onClickCard,
  onClickTripName,
  variant,
}: Props & MappedStateProps) {
  const openModal = useOpenModal()
  const viewAsTemplate = useContext(TripContext)?.viewAsTemplate

  const timeFormat = useRegionTimeFormat()

  switch (item.type) {
    case 'FLIGHT':
      if (viewAsTemplate && !standaloneFlightsEnabled) {
        return null
      }
      if (item.isBooked || item.sourceType !== 'LE_Flight') {
        return (
          <FlightItemCard
            item={item}
            onClick={() => openModal(TripItemSummary, { item })}
            variant={variant}
            onClickTripName={onClickTripName}
          />
        )
      }

      // flight items saved from checkout have both dates and times
      if (item.startDate && item.startTime && item.endDate && item.endTime) {
        return (
          <FlightItemCard
            item={item}
            onClick={() => openModal(TripItemSummary, { item })}
            variant={variant}
            onClickTripName={onClickTripName}
          />
        )
      }

      if (!isLeFlightItem(item)) {
        return null
      }
      return (
        <FlightSearchTile
          item={item}
          variant={variant}
          onClickTripName={onClickTripName}
        />
      )
    case 'HOTEL':
      return (
        <HotelItemCard
          item={item}
          onClickCard={onClickCard}
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'AIRBNB':
      return (
        <ItineraryItemCard
          item={item}
          name={item.place.name}
          placeId={'id' in item.place ? item.place.id : undefined}
          locationDescription={item.place.region}
          descriptors={[formatShortDateRange(item)]}
          address={item.place.address}
          confirmationCode={item.confirmationCode}
          confirmationCodeLabel="Confirmation number"
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'NOTE':
      return (
        <NoteItemCard
          item={item}
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'RESTAURANT_BAR':
      return (
        <ItineraryItemCard
          item={item}
          name={item.place.name}
          placeId={'id' in item.place ? item.place.id : undefined}
          locationDescription={item.place.region}
          descriptors={[formatDateTimeOrDateRange(item, timeFormat)]}
          address={item.place.address}
          confirmationCode={item.confirmationCode}
          confirmationCodeLabel="Confirmation number"
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'ATTRACTION':
      return (
        <ItineraryItemCard
          item={item}
          name={item.place.name}
          placeId={'id' in item.place ? item.place.id : undefined}
          locationDescription={item.place.region}
          descriptors={[formatDateTimeOrDateRange(item, timeFormat)]}
          address={item.place.address}
          confirmationCode={item.confirmationCode}
          confirmationCodeLabel="Confirmation number"
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'ACTIVITY':
    case 'THING_TO_DO':
      return (
        <ActivityItemCard
          item={item}
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'CAR_RENTAL':
      return <CarRentalItemCard item={item} />
    case 'CRUISE':
      return (
        <CruiseItemCard
          item={item}
          onClickCard={onClickCard}
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'TOUR':
      return (
        <TourItemCard
          item={item}
          onClickCard={onClickCard}
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'EVENT':
      return (
        <ItineraryItemCard
          item={item}
          name={item.name}
          locationDescription={item.place.region}
          descriptors={[
            formatMultiDatesDays({
              item,
              timeFormat,
            }),
          ]}
          placeId={'id' in item.place ? item.place.id : undefined}
          imgSrc={item.place.type == 'GEO' ? item.place.photoUrl : undefined}
          address={item.place.address}
          confirmationCode={item.confirmationCode}
          confirmationCodeLabel="Confirmation number"
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'EXPERIENCE':
      return (
        <ExperienceItemCard
          item={item}
          onClickCard={onClickCard}
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    case 'FERRY':
    case 'BUS':
    case 'TRAIN':
    case 'OTHER_TRANSPORT':
      return (
        <TransportItemCard
          item={item}
          onClickTripName={onClickTripName}
          variant={variant}
        />
      )
    default:
      return (
        <Card onClick={() => openModal(TripItemSummary, { item })}>
          <CardContainer>
            <Heading variant="heading6">Unknown card type</Heading>
          </CardContainer>
        </Card>
      )
  }
}

const ItemCard = ({
  item,
  standaloneFlightsEnabled,
  onRender,
  onClickCard,
  onClickTripName,
  variant,
}: Props & MappedStateProps & { onRender?: (sectionId: string) => void }) => {
  const sectionId = itemSectionId(item.id)
  useEffect(() => onRender?.(sectionId))
  return (
    <div>
      <ScrollAnchor id={sectionId} vOffset={rem(156)} />
      <SectionIdProvider sectionId={sectionId}>
        <InnerItemCard
          item={item}
          standaloneFlightsEnabled={standaloneFlightsEnabled}
          onClickCard={onClickCard}
          onClickTripName={onClickTripName}
          variant={variant}
        />
      </SectionIdProvider>
    </div>
  )
}

export default connect<MappedStateProps, unknown, Props, App.State>(
  (state) => ({
    standaloneFlightsEnabled: isStandaloneFlightsEnabled(state),
  }),
)(ItemCard)
